import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Phone, Mail, MapPin, Calendar, Shield, Edit2, Save, X, Loader2 } from "lucide-react";

const EmployeeInfo = () => {
  const { user, getAuthHeaders } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    password: "",
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        address: user.address || "",
        password: "",
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        address: formData.address,
      };
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/employees/${user.id}`, updateData, {
        headers: getAuthHeaders(),
        
      });
      toast.success("Information updated successfully!");
      setEditing(false);
      setFormData((prev) => ({ ...prev, password: "" }));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to update information");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      phone: user?.phone || "",
      address: user?.address || "",
      password: "",
    });
    setEditing(false);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="employee-info-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">My Information</h1>
          <p className="text-[#666666]">View and update your personal details</p>
        </div>
        {!editing ? (
          <Button
            onClick={() => setEditing(true)}
            className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
            data-testid="edit-info-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Info
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="border-[#E5E5E5]"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="save-info-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="border border-[#E5E5E5] shadow-none lg:col-span-1">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-[#6A1E2E] flex items-center justify-center text-white text-3xl font-bold mb-4">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <h2 className="text-xl font-bold text-[#1A1A1A]">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-sm text-[#666666]">{user?.email}</p>
              {user?.role === "admin" && (
                <span className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-[#F0EAEB] text-[#6A1E2E] text-xs font-semibold rounded-sm">
                  <Shield className="w-3 h-3" />
                  Administrator
                </span>
              )}
            </div>

            {user?.positions && user.positions.length > 0 && (
              <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
                <p className="text-xs uppercase tracking-wider text-[#666666] mb-2">Positions</p>
                <div className="flex flex-wrap gap-2">
                  {user.positions.map((pos, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-[#FAFAFA] text-[#1A1A1A] text-xs rounded-sm border border-[#E5E5E5]"
                    >
                      {pos}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="border border-[#E5E5E5] shadow-none lg:col-span-2">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
              <User className="w-5 h-5 text-[#6A1E2E]" />
              Personal Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-xs uppercase tracking-wider text-[#666666]">
                  First Name
                </Label>
                {editing ? (
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                    data-testid="input-first-name"
                  />
                ) : (
                  <p className="text-[#1A1A1A] font-medium py-2">{user?.first_name || "—"}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-xs uppercase tracking-wider text-[#666666]">
                  Last Name
                </Label>
                {editing ? (
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                    data-testid="input-last-name"
                  />
                ) : (
                  <p className="text-[#1A1A1A] font-medium py-2">{user?.last_name || "—"}</p>
                )}
              </div>
            </div>

            {/* Email - Read only */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#666666] flex items-center gap-2">
                <Mail className="w-3 h-3" />
                Email
              </Label>
              <p className="text-[#1A1A1A] font-medium py-2">{user?.email || "—"}</p>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-[#666666] flex items-center gap-2">
                <Phone className="w-3 h-3" />
                Phone Number
              </Label>
              {editing ? (
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-phone"
                />
              ) : (
                <p className="text-[#1A1A1A] font-medium py-2">{user?.phone || "—"}</p>
              )}
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address" className="text-xs uppercase tracking-wider text-[#666666] flex items-center gap-2">
                <MapPin className="w-3 h-3" />
                Address
              </Label>
              {editing ? (
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-address"
                />
              ) : (
                <p className="text-[#1A1A1A] font-medium py-2">{user?.address || "—"}</p>
              )}
            </div>

            {/* Date of Hire - Read only */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#666666] flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                Date of Hire
              </Label>
              <p className="text-[#1A1A1A] font-medium py-2">{user?.date_of_hire || "—"}</p>
            </div>

            {/* Password Change */}
            {editing && (
              <div className="space-y-2 pt-4 border-t border-[#E5E5E5]">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#666666]">
                  New Password (leave blank to keep current)
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-password"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeInfo;
